import React from 'react';

interface OpsAssistPanelProps {
  aiAssistLoading: boolean;
  aiAssistError: string | null;
  aiSuggestion: string;
  aiDelayRevealTriggered: boolean;
  aiDelayRevealReason: string;
  assistRankingsLoading: boolean;
  rankedAssistRows: any[];
  currentStepId: number | null;
  onMicroAssist: (actionType: string) => void;
  onOpenExplainToClass: () => void;
  onOpenTeacherAi: () => void;
  onCodingStepByStep: () => void;
  renderAssistFeedbackControls: () => React.ReactNode;
}

export const OpsAssistPanel: React.FC<OpsAssistPanelProps> = ({
  aiAssistLoading,
  aiAssistError,
  aiSuggestion,
  aiDelayRevealTriggered,
  aiDelayRevealReason,
  assistRankingsLoading,
  rankedAssistRows,
  currentStepId,
  onMicroAssist,
  onOpenExplainToClass,
  onOpenTeacherAi,
  onCodingStepByStep,
  renderAssistFeedbackControls,
}) => (
  <div className="bg-white rounded-lg border border-gray-200 p-5">
    <div className="font-medium text-gray-900">AI micro-assists</div>
    <div className="text-xs text-gray-600 mt-1">Suggestions only. Nothing is student-facing until you act on them.</div>
    <div className="flex flex-wrap gap-2 mt-3">
      <button
        onClick={() => onMicroAssist('another_example')}
        disabled={aiAssistLoading}
        className="bg-white border px-3 py-2 rounded text-sm hover:bg-gray-100 disabled:opacity-50"
        type="button"
      >
        Another example
      </button>
      <button
        onClick={() => onMicroAssist('rephrase_grade_level')}
        disabled={aiAssistLoading}
        className="bg-white border px-3 py-2 rounded text-sm hover:bg-gray-100 disabled:opacity-50"
        type="button"
      >
        Rephrase for grade level
      </button>
      <button
        onClick={() => onMicroAssist('quick_check')}
        disabled={aiAssistLoading}
        className="bg-white border px-3 py-2 rounded text-sm hover:bg-gray-100 disabled:opacity-50"
        type="button"
      >
        Quick check
      </button>
      <button
        onClick={onOpenExplainToClass}
        disabled={!currentStepId || aiAssistLoading}
        className="bg-white border px-3 py-2 rounded text-sm hover:bg-gray-100 disabled:opacity-50"
        type="button"
      >
        Explain to Class
      </button>
      <button
        onClick={onOpenTeacherAi}
        disabled={!currentStepId || aiAssistLoading}
        className="bg-white border px-3 py-2 rounded text-sm hover:bg-gray-100 disabled:opacity-50"
        type="button"
      >
        Teacher AI
      </button>
      <button
        onClick={() => onMicroAssist('common_misconception')}
        disabled={aiAssistLoading}
        className="bg-white border px-3 py-2 rounded text-sm hover:bg-gray-100 disabled:opacity-50"
        type="button"
      >
        Common misconception
      </button>
      <button
        onClick={() => onMicroAssist('connect_to_apply')}
        disabled={aiAssistLoading}
        className="bg-white border px-3 py-2 rounded text-sm hover:bg-gray-100 disabled:opacity-50"
        type="button"
      >
        Connect to Apply
      </button>
      <button
        onClick={onCodingStepByStep}
        disabled={aiAssistLoading}
        className="bg-white border px-3 py-2 rounded text-sm hover:bg-gray-100 disabled:opacity-50"
        type="button"
      >
        Coding step-by-step
      </button>
    </div>
    {aiAssistError && (
      <div className="mt-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        {aiAssistError}
      </div>
    )}
    {aiSuggestion ? (
      <div className="mt-3 border rounded p-3 bg-gray-50">
        <div className="text-xs font-medium text-gray-700">Suggestion</div>
        <div className="text-sm text-gray-800 mt-1 whitespace-pre-wrap">{aiSuggestion}</div>
        {aiDelayRevealTriggered && aiDelayRevealReason ? (
          <div className="mt-3 rounded border border-gray-200 bg-white p-3">
            <div className="text-xs font-medium text-gray-700">Why delay reveal</div>
            <div className="text-sm text-gray-700 mt-1">{aiDelayRevealReason}</div>
          </div>
        ) : null}
        {renderAssistFeedbackControls()}
      </div>
    ) : null}
    <div className="mt-3 rounded border border-gray-200 bg-white p-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">Assist rankings (this run)</div>
      {assistRankingsLoading ? (
        <div className="mt-1 text-xs text-gray-500">Loading rankings…</div>
      ) : rankedAssistRows.length === 0 ? (
        <div className="mt-1 text-xs text-gray-500">No ratings yet.</div>
      ) : (
        <div className="mt-2 space-y-1">
          {rankedAssistRows.map((row: any) => (
            <div key={`assist-rank-${String(row?.action_type || '')}`} className="text-xs text-gray-700">
              <span className="font-medium text-gray-800">{String(row?.action_type || 'unknown').replace(/_/g, ' ')}</span>
              <span className="ml-2 text-gray-500">
                score {Math.round(Number(row?.score || 0) * 100)}% • 👍 {Number(row?.thumbs_up || 0)} / 👎 {Number(row?.thumbs_down || 0)} • used {Number(row?.used_in_class || 0)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);
